import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './DeleteAccountPage.module.css';

// === /delete-account — публичная страница удаления аккаунта (требование Google Play) ===
//
// Зачем отдельная страница, если удаление и так есть в профиле (ProfilePage «Опасная зона»):
// Google требует, чтобы человек мог НАЙТИ путь удаления, НЕ устанавливая приложение —
// адрес указывается прямо в карточке Play и должен открываться у любого. Страница внутри
// личного кабинета этого не даёт: чтобы её увидеть, надо уже знать, где искать.
//
// Поэтому здесь: что именно удаляется, что остаётся и почему, сколько это занимает —
// и две двери. Помнишь пароль → входишь и удаляешь сам. Не помнишь (телефон потерян,
// почта недоступна) → пишешь на support@, иначе человек упирается в тупик.
//
// Список того, что удаляется, обязан совпадать с User.anonymize() (server/models/User.js)
// и со строкой «Удалённый аккаунт» в Политике конфиденциальности. Меняешь одно — правь всё.

const REMOVED = [
  'Имя, ник и адрес электронной почты',
  'Фотография профиля',
  'Привязки входа через Apple, Google и Facebook',
  'Список друзей и заявки в друзья',
  'Адрес последнего входа и признак устройства',
  'Все входы на всех устройствах — сессии закрываются сразу',
];

const KEPT = [
  {
    what: 'Результаты сыгранных партий',
    why: 'остаются обезличенными, без связи с Вами — иначе у соперников пропадёт их собственная история и рейтинг',
  },
  {
    what: 'Записи о платежах',
    why: 'хранятся 7 лет: этого требует налоговое и бухгалтерское законодательство, и удалить их мы не вправе',
  },
];

const DeleteAccountPage = () => {
  // Разрешаем вертикальный скролл: у body по умолчанию overflow:hidden (игровой стол
  // не должен ездить), и без этого страница обрезалась на «Что удаляется» — до кнопок
  // и адреса поддержки было не долистать. Найдено Адеком 05.09.2026.
  useEffect(() => {
    document.body.classList.add('legal-scroll');
    window.scrollTo(0, 0);
    return () => document.body.classList.remove('legal-scroll');
  }, []);

  return (
  <div className={styles.page}>
    <div className={styles.inner}>
      <Link to="/" className={styles.brand}>BlotMe</Link>

      <h1 className={styles.title}>Удаление аккаунта</h1>
      <p className={styles.lead}>
        Аккаунт BlotMe можно удалить в любой момент. Здесь написано, что именно исчезает,
        что остаётся и как это сделать.
      </p>

      <section className={styles.block}>
        <h2 className={styles.h2}>Как удалить</h2>

        <div className={styles.card}>
          <div className={styles.cardTitle}>В приложении</div>
          <p className={styles.cardText}>
            Профиль → <b>Опасная зона</b> → <b>Удалить аккаунт</b>. Приложение попросит
            подтверждение: пароль, а если Вы входили через Apple, Google или Facebook —
            слово <b>УДАЛИТЬ</b>.
          </p>
        </div>

        <div className={styles.card}>
          <div className={styles.cardTitle}>В браузере, без установки</div>
          <p className={styles.cardText}>
            Войдите на сайте и откройте свой профиль — там та же кнопка.
          </p>
          <Link to="/profile" className={styles.btn}>Войти и открыть профиль</Link>
        </div>

        <div className={styles.card}>
          <div className={styles.cardTitle}>Если войти не получается</div>
          <p className={styles.cardText}>
            Потеряли доступ к почте или устройству — напишите нам, и мы удалим аккаунт сами.
            Укажите ник в игре и адрес почты, на который он был заведён: без этого мы не сможем
            понять, чей аккаунт удалять.
          </p>
          <a href="mailto:support@blotme.app?subject=Удаление%20аккаунта" className={styles.btn}>
            support@blotme.app
          </a>
          <p className={styles.note}>Отвечаем в течение 30 дней, обычно быстрее.</p>
        </div>
      </section>

      <section className={styles.block}>
        <h2 className={styles.h2}>Что удаляется</h2>
        <p className={styles.lead}>
          Сразу после подтверждения вход в аккаунт прекращается, а профиль обезличивается:
        </p>
        <ul className={styles.list}>
          {REMOVED.map((item) => (
            <li key={item} className={styles.removed}>{item}</li>
          ))}
        </ul>
      </section>

      <section className={styles.block}>
        <h2 className={styles.h2}>Что остаётся и почему</h2>
        <ul className={styles.list}>
          {KEPT.map(({ what, why }) => (
            <li key={what} className={styles.kept}>
              <b>{what}</b> — {why}.
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.block}>
        <h2 className={styles.h2}>Что важно знать заранее</h2>
        <ul className={styles.list}>
          <li className={styles.warn}>
            <b>Вернуть аккаунт нельзя.</b> Прогресс, рейтинг и друзья не восстанавливаются
            даже при обращении в поддержку.
          </li>
          <li className={styles.warn}>
            <b>Зарегистрироваться заново можно</b> на ту же почту и с тем же ником —
            они освобождаются. Но это будет новый аккаунт с нуля.
          </li>
        </ul>
      </section>

      <p className={styles.footer}>
        Подробности о сроках хранения — в <Link to="/privacy" className={styles.link}>Политике
        конфиденциальности</Link>, раздел 5. Вопросы —{' '}
        <a href="mailto:privacy@blotme.app" className={styles.link}>privacy@blotme.app</a>.
      </p>
    </div>
  </div>
);
};

export default DeleteAccountPage;
